import { EntityLinks, Goal, GoalProgressCount, ID, Task } from '@griply/types';

import { taskGoalIds } from '../links/task-link-ids';
import { isCountableGoalTask } from '../task-predicates';
import { buildSubGoalProgressCounts } from './build-sub-goal-progress-counts';
import { getGoalParentIds } from './get-goal-parents';

const emptyCount: GoalProgressCount = { completed: 0, total: 0 };

export type BuildGoalProgressCountsOptions = {
  // Required: without it every relationship-linked goal drops out of the rollup and the sub-goal sets.
  links: EntityLinks;
};

export const buildGoalProgressCounts = (
  tasks: Task[],
  goals: Goal[],
  { links }: BuildGoalProgressCountsOptions,
): Map<ID, GoalProgressCount> => {
  const directCounts = new Map<ID, GoalProgressCount>();

  // Through the gate: a migrated goal's chain is frozen, so it rolls up into a stale parent.
  const parentsByGoalId = new Map<ID, ID[]>(
    goals.map((goal) => [goal.id, getGoalParentIds(goal, { links })]),
  );

  const collectAncestors = (ids: ID[], seen: Set<ID>): Set<ID> => {
    const next = ids.filter((id) => !seen.has(id));
    if (!next.length) {
      return seen;
    }

    next.forEach((id) => seen.add(id));

    return collectAncestors(
      next.flatMap((id) => parentsByGoalId.get(id) ?? []),
      seen,
    );
  };

  const ancestorsById = new Map<ID, Set<ID>>();
  const ancestorsOf = (goalId: ID): Set<ID> => {
    const cached = ancestorsById.get(goalId);
    if (cached) {
      return cached;
    }

    const resolved = collectAncestors(
      parentsByGoalId.get(goalId) ?? [],
      new Set<ID>(),
    );
    ancestorsById.set(goalId, resolved);

    return resolved;
  };

  // Deepest only: an id another already reaches lands in that subtree twice, direct and rolled up.
  const deepestOf = (ids: ID[]): ID[] => {
    if (ids.length < 2) {
      return ids;
    }

    return ids.filter(
      (id) =>
        !ids.some((otherId) => otherId !== id && ancestorsOf(otherId).has(id)),
    );
  };

  const countableGoalIds = (task: Task): ID[] => deepestOf(taskGoalIds(task));

  tasks.forEach((task) => {
    if (!isCountableGoalTask(task)) {
      return;
    }

    // A task linked to several unrelated goals counts toward each of their progress bars.
    countableGoalIds(task).forEach((goalId) => {
      const current = directCounts.get(goalId) ?? emptyCount;
      directCounts.set(goalId, {
        completed: current.completed + (task.completedAt ? 1 : 0),
        total: current.total + 1,
      });
    });
  });

  const childrenByParent = new Map<ID, Goal[]>();
  goals.forEach((goal) => {
    // A goal rolls up into every parent it hangs under, the way it renders under every one.
    deepestOf(parentsByGoalId.get(goal.id) ?? []).forEach((parentId) => {
      const siblings = childrenByParent.get(parentId) ?? [];
      siblings.push(goal);
      childrenByParent.set(parentId, siblings);
    });
  });

  const rolledCounts = new Map<ID, GoalProgressCount>();
  const inProgress = new Set<ID>();

  const rollUp = (goalId: ID): GoalProgressCount => {
    const cached = rolledCounts.get(goalId);
    if (cached) {
      return cached;
    }

    // corrupt parent links can form a cycle — a re-entered goal contributes nothing
    if (inProgress.has(goalId)) {
      return emptyCount;
    }

    inProgress.add(goalId);

    const rolled = (childrenByParent.get(goalId) ?? []).reduce(
      (accumulator, child) => {
        const childCount = rollUp(child.id);

        return {
          completed: accumulator.completed + childCount.completed,
          total: accumulator.total + childCount.total,
        };
      },
      directCounts.get(goalId) ?? emptyCount,
    );

    inProgress.delete(goalId);
    rolledCounts.set(goalId, rolled);

    return rolled;
  };

  const counts = new Map<ID, GoalProgressCount>();
  goals.forEach((goal) => {
    const count = rollUp(goal.id);
    if (!count.total) {
      return;
    }

    counts.set(goal.id, count);
  });

  return buildSubGoalProgressCounts(goals, counts, { links });
};
